import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { UsernameLookupResult } from '@/client/users';
import { LoadingStateType, LoadingStatuses } from '@/common/constants';
import { useNotifications } from '@/hooks/notifications.hook';
import { usePermanentlyRemoveUser } from '@/hooks/query';
import { useToast } from '@/hooks/useToast';
import { AxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type UseUsernameConflictResolutionParams = {
  accountId?: string;
  onResolved: () => void;
};

export const useUsernameConflictResolution = ({
  accountId,
  onResolved,
}: UseUsernameConflictResolutionParams) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { permanentlyRemove } = usePermanentlyRemoveUser();

  const [userToDelete, setUserToDelete] = useState<UsernameLookupResult | null>(
    null,
  );
  const [deleteState, setDeleteState] = useState<LoadingStateType>(
    LoadingStatuses.IDLE,
  );

  const deletingRef = useRef(false);

  const {
    lastMessage,
    setNotificationParam: setUserId,
    notificationParam: notificationUserId,
    waitForConnection,
  } = useNotifications(client.users.userNotifyUrl);

  useEffect(() => {
    if (!notificationUserId || !deletingRef.current || !lastMessage) return;

    if (lastMessage.data?.payload?.id !== notificationUserId) return;

    if (lastMessage.data?.event === 'user.delete.finished') {
      deletingRef.current = false;
      setDeleteState(LoadingStatuses.IDLE);
      setUserId(undefined);
      toast?.success(t('toast.success'), t('user.permanentlyDeleted'));
      onResolved();
    }

    if (lastMessage.data?.event === 'user.delete.error') {
      deletingRef.current = false;
      setDeleteState(LoadingStatuses.IDLE);
      setUserId(undefined);
      toast?.error(t('toast.error'), t('user.not.deleted'));
    }
  }, [notificationUserId, lastMessage]);

  const confirmDelete = async (userId: string) => {
    if (!accountId) return;

    setUserToDelete(null);
    setDeleteState(LoadingStatuses.LOADING);
    deletingRef.current = true;
    setUserId(userId);

    const connected = await waitForConnection();

    if (!connected) {
      deletingRef.current = false;
      setDeleteState(LoadingStatuses.IDLE);
      setUserId(undefined);
      toast?.error(t('toast.error'), t('user.bulk.connectionUnavailable'));

      return;
    }

    try {
      await permanentlyRemove({ userId, accountId });
    } catch (e) {
      deletingRef.current = false;
      setDeleteState(LoadingStatuses.IDLE);
      setUserId(undefined);
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return {
    userToDelete,
    requestDelete: setUserToDelete,
    cancelDelete: () => setUserToDelete(null),
    confirmDelete,
    deleteState,
  };
};
